import _ from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { getArchivedProjects } from '../../../services/projectService';

import Button from '../../themed/button';
import Header from '../../blocks/header';
import Input from '../../themed/input/input';
import NoItems from '../../blocks/no-items/';
import Pagination from '../../themed/pagination';
import Select from '../../themed/select/select';
import SyncTime from '../../blocks/sync-time';
import Table from '../../common/table';

import css from './style.module.scss';
import logger from '../../../services/logService';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface ArchivedProject {
	dateArchived: string;
	contactPersonName: string;
	createdAt: string;
	deletedById: string;
	deletedByName: string;
	projectId: string;
	projectTitle: string;
	updatedAt: string;
	_id: string;
}

const Archived: React.FC = () => {
	const [dateRange, setDateRange] = useState('');
	const [sortColumn, setSortColumn] = useState(initialSortColumn);
	const [searchQuery, setSearchQuery] = useState('');
	const [searchOption, setSearchOption] = useState('projectTitle');
	const [isLoading, setLoading] = useState(false);
	const [archivedProjects, setArchivedProjects] = useState<ArchivedProject[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(10);
	const [lastUpdated, setLastUpdated] = useState<number | null>(null);

	const filteredProjects = useMemo(() => {
		const searchable: string = searchQuery.toLowerCase();
		if (!searchable) return archivedProjects;

		return archivedProjects.filter((i: any) => {
			return _.get(i, searchOption).toLowerCase().indexOf(searchable) !== -1;
		});
	}, [archivedProjects, searchOption, searchQuery]);

	const projectsWithinDateRange = useMemo(() => {
		if (dateRange) {
			const dateBefore = utils.getDaysBeforeFromCurrentDate(+dateRange);
			return filteredProjects.filter((project) => {
				return utils.getIsBefore(project.dateArchived, new Date()) && utils.getIsAfter(project.dateArchived, dateBefore.toString());
			});
		}

		return filteredProjects;
	}, [filteredProjects, dateRange]);

	const paginatedData = useMemo(() => {
		let filtered = projectsWithinDateRange;

		const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
		const paginated = utils.paginate(sorted, currentPage, pageSize);

		return { totalCount: filtered.length, paginated };
	}, [currentPage, projectsWithinDateRange, pageSize, sortColumn]);

	const fetchArchived = useCallback(async () => {
		setLoading(true);

		try {
			const response = await getArchivedProjects<ArchivedProject>();
			setArchivedProjects(response.data.data.archivedProjects);
			setLoading(false);
			setLastUpdated(Date.now());
		} catch (error) {
			setLoading(false);

			const cachedData = utils.getCachedDataFromException(error);
			if (!cachedData) return logger.log(error as Error);

			setArchivedProjects(cachedData.data.archivedProjects);
		}
	}, []);

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = +e.target.value;
		const actualPageSize: number = pageSize === 0 ? 1 : pageSize;

		setPageSize(actualPageSize);
	};

	const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSearchOption(e.target.value);
	};

	const handleSetSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
		setCurrentPage(1);
	};
	
	const handleSetDateRange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setDateRange(e.target.value);
		setCurrentPage(1);
	};

	useEffect(() => {
		fetchArchived();
	}, [fetchArchived]);

	return (
		<Fragment>
			<Header subtitle="View Archive" />

			<div className={css['projects-body']}>
				<SyncTime lastSynced={lastUpdated} />

				<div className="divider my-4"></div>

				<div className="d-flex justify-content-between align-items-center">
					<div className={css['projects-body-title']}>
						<h2>Archived Projects <span>{archivedProjects.length}</span></h2>
					</div>

					<div className="d-flex justify-content-center align-items-center">
						<div className={css['projects-date-range']}>
							<div className="me-3">
								<Select
									name="date-range"
									placeholder="Select Date Range"
									options={dateRangeOptions}
									value={dateRange}
									onChange={handleSetDateRange}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className={css['projects-body-table']}>
					<div className="d-flex justify-content-between mb-2">
						<div className="d-flex justify-content-end">
							<div className="me-3">
								<Select
									name="page-size"
									placeholder="Search By"
									options={searchOptions}
									value={searchOption}
									onChange={handleOptionChange}
								/>
							</div>

							<div>
								<Input
									type="text"
									name="search"
									autoComplete="new-password"
									value={searchQuery}
									onChange={handleSetSearch}
									placeholder="Enter to search"
								/>
							</div>
						</div>

						<div className={css['projects-refresh']}>
							<Button
								onClick={fetchArchived}
								label="Refresh"
								icon="arrow-clockwise"
							/>
						</div>
					</div>

					<div className="divider my-4"></div>

					<Table
						columns={columns}
						data={paginatedData.paginated}
						sortColumn={sortColumn}
						onSort={setSortColumn}
						isFetching={isLoading}
					/>

					<NoItems
						isVisible={archivedProjects.length === 0}
						onRefresh={fetchArchived}
					/>

					<div className="d-flex justify-content-between align-items-center mt-3">
						<Select
							name="page-size"
							placeholder={'Showing (' + pageSize + 'rows)'}
							options={utils.transformPageRange(20)}
							value={pageSize}
							onChange={handlePageSizeChange}
						/>

						<Pagination
							itemsCount={paginatedData.totalCount}
							pageSize={pageSize}
							currentPage={currentPage}
							onPageChange={(page: number) => setCurrentPage(page)}
						/>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

const columns: models.Column[] = [
	{
		path: 'deletedByName',
		label: 'Archived By',
	},
	{
		path: 'dateArchived',
		label: 'Date Archived',
		content: (item: ArchivedProject) => (
			<span>{item.dateArchived ? utils.formatTime(item.dateArchived) : 'N/A'}</span>
		),
	},
	{
		path: 'projectTitle',
		label: 'Project Title',
	},
	{
		path: 'createdAt',
		label: 'Date Created',
		content: (item: ArchivedProject) => (
			<span>{utils.formatTime(item.createdAt)}</span>
		),
	},
	{
		path: 'updatedAt',
		label: 'Last Updated',
		content: (item: ArchivedProject) => (
			<span>{utils.formatTime(item.updatedAt)}</span>
		),
	},
	{ path: 'contactPersonName', label: 'Contact Person Name' },
];

const searchOptions: models.SelectOption[] = [
	{ id: 'deletedByName', name: 'Archived By' },
	{ id: 'projectTitle', name: 'Project Title' },
	{ id: 'contactPersonName', name: 'Customer Contact Person' },
];

const dateRangeOptions: models.SelectOption[] = [
	{ id: '', name: 'All Archived Projects' },
	{ id: '15', name: 'Archived within the past 15 days' },
	{ id: '30', name: 'Archived within the past 30 days' },
	{ id: '60', name: 'Archived within the past 2 months' },
	{ id: '90', name: 'Archived within the past 3 months' },
	{ id: '120', name: 'Archived within the past 4 months' },
];

const initialSortColumn: models.SortColumn = {
	path: 'updatedAt',
	order: 'desc',
};

export default Archived;
